'use client';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { requestResetPasswordEmail } from '@/lib/api/request-reset-password-email';
import { useAppDispatch } from '@/lib/client-hook/use-app-dispatch';
import { useDictionary } from '@/lib/context/locale-provider';
import { getCookie, setCookie } from '@/lib/cookies';
import { Languages } from '@/lib/dictionaries';
import { setErrorMessage } from '@/slice/error';
import Image from 'next/image';
import React, { useRef } from 'react';
import backIcon from '@/public/images/ic_paging_prev.svg';

function ChangePassword({ lang }: { lang: Languages }) {
  const dict = useDictionary();
  const dispatch = useAppDispatch();

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleSubmit = async () => {
    const email = inputRef.current?.value;

    if (!email) {
      return;
    }

    const cookieKey = `change-password-email-${email}`;

    // 비밀번호 재설정 이메일 발송 횟수 읽기
    let attempts = parseInt(getCookie(cookieKey) || '0', 10);

    if (attempts >= 5) {
      dispatch(setErrorMessage(dict!.passwordReset.overFailed));
      return;
    }

    const res = await requestResetPasswordEmail(lang, email);
    const result = await res.json();
    if (!res.ok) {
      attempts += 1;
      setCookie(cookieKey, attempts.toString());
      if (res.status === 404) {
        dispatch(setErrorMessage(dict!.passwordReset.noUser));
      } else if (res.status === 504) {
        dispatch(setErrorMessage(dict!.passwordReset.unableSendEmail));
      }
    } else {
      dispatch(setErrorMessage(dict!.passwordReset.success));
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" className="underline underline-offset-2">
          {dict!.login.changePassword}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="w-[calc(100%-40px)] max-w-[600px] h-[800px] flex flex-col gap-[88px]">
        <AlertDialogTitle className="relative flex items-center">
          <span className="mr-auto">
            <AlertDialogCancel>
              <Image src={backIcon} alt="Close" width={27} height={27} />
            </AlertDialogCancel>
          </span>
          <p className="absolute left-1/2 -translate-x-1/2">
            {dict!.login.changePassword}
          </p>
        </AlertDialogTitle>
        <div className="flex flex-col justify-start items-center gap-2.5">
          <div className="grid w-full items-start gap-1.5 max-w-[360px]">
            <Label htmlFor="email">{dict!.common.email}</Label>
            <Input
              type="email"
              placeholder={dict!.placeholder.email}
              ref={inputRef}
            />
          </div>
          <Button
            className="bg-gradient-giftifan max-w-[360px] w-full h-[50px]"
            onClick={handleSubmit}
          >
            {dict!.passwordReset.buttonName}
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default ChangePassword;
